import React, { useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { removeRecentPhrase, clearRecentPhrases } from '@/store/slices/uiSlice';
import { selectFile } from '@/store/slices/filesSlice';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Hash, Clock, X, MoreVertical, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

interface RecentPhrasesProps {
  className?: string;
}

export function RecentPhrases({ className }: RecentPhrasesProps) {
  const dispatch = useAppDispatch();
  const { recentPhrases } = useAppSelector((state) => state.ui);
  const [isExpanded, setIsExpanded] = React.useState(true);

  const handlePhraseClick = useCallback(
    (phraseId: string) => {
      dispatch(selectFile(phraseId));
    },
    [dispatch]
  );

  const handleRemovePhrase = useCallback(
    (phraseId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      dispatch(removeRecentPhrase(phraseId));
    },
    [dispatch]
  );

  const handleClearAll = useCallback(() => {
    dispatch(clearRecentPhrases());
  }, [dispatch]);

  const formatLastUsed = (lastUsed: string) => {
    const date = new Date(lastUsed);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getDisplayTitle = (phrase: (typeof recentPhrases)[0]) => {
    return (
      phrase.title ||
      phrase.path
        .split('/')
        .pop()
        ?.replace(/\.(md|txt)$/, '') ||
      'Untitled'
    );
  };

  if (recentPhrases.length === 0) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className={cn('border-t bg-card/25', className)}>
        {/* Header */}
        <div className="p-3 border-b bg-background/50">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 p-0 h-auto font-medium text-foreground"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <Hash className="h-4 w-4" />
              <span>Recent Phrases</span>
              <Badge variant="secondary" className="ml-2 text-xs">
                {recentPhrases.length}
              </Badge>
            </Button>

            {isExpanded && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleClearAll}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Phrases List */}
        {isExpanded && (
          <ScrollArea className="max-h-48">
            <div className="p-2">
              {recentPhrases.map((phrase) => (
                <div
                  key={phrase.id}
                  className={cn(
                    'flex items-center gap-3 p-2 rounded-md cursor-pointer group',
                    'hover:bg-accent transition-colors'
                  )}
                  onClick={() => handlePhraseClick(phrase.id)}
                >
                  <Hash className="h-4 w-4 text-muted-foreground shrink-0" />

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium truncate mb-1">{getDisplayTitle(phrase)}</h4>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{formatLastUsed(phrase.lastUsed)}</span>

                      {phrase.path && (
                        <>
                          <span>•</span>
                          <span className="truncate font-mono text-xs">
                            @{phrase.path.replace(/\.(md|txt)$/, '')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleRemovePhrase(phrase.id, e)}
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Remove from recent</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}

              {recentPhrases.length >= 10 && (
                <div className="px-2 py-1 text-center">
                  <p className="text-xs text-muted-foreground">Showing recent 10 phrases</p>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </TooltipProvider>
  );
}
